<script setup lang="ts">
/* eslint-disable max-lines */
import { computed, onMounted, ref, watch } from 'vue'
import {
  constructionMethodOption,
  mixedOption,
  OTHER_LABEL
} from '@/constants/ui/inspection/buildingInfo/constructionMethodLabel'
import { buildingUsageOption } from '@/constants/ui/inspection/buildingInfo/buildingUsageLabel'
import {
  CONSTRUCTION_METHOD,
  BUILDING_USAGE,
  WOODEN_FLOORS,
  CONSTRUCTION_METHOD_MIXED
} from '@/constants/accessor/api/emergencyInspectionResult/buildingOverview'
import { woodenFloorsOption } from '@/constants/ui/inspection/buildingInfo/woodenFloorsLabel'
import { CONSTRUCTION_TYPE } from '@/constants/accessor/api/emergencyInspectionResult/assessmentInfo/constructionType'
import { useInspectionResultStore } from '@/stores/inspectionResultUpsertStore'
import type {
  BuildingUsage,
  ConstructionMethod,
  ConstructionMethodMixed,
  FloorCountWood
} from '@/types/accessor/api/emergencyInspectionResult/buildingOverview'
import LabelContainer from '@/components/inspection/common/LabelContainer.vue'
import { BUILDING_LOCATION_SPECIFICATION_LABEL } from '@/constants/ui/inspection/buildingInfo/buildingLocationSpecification'

const emit = defineEmits<{
  (event: 'updateUnfilledList', unfilledList: string[]): void
  (event: 'onValidationResult', isValid: boolean): void
  (event: 'savedInspectionResultStore'): void
}>()

const props = withDefaults(
  defineProps<{
    triggerValidate: boolean
    saveToStoreFlag: boolean
  }>(),
  {
    triggerValidate: false,
    saveToStoreFlag: false
  }
)

const inspectionResultStore = useInspectionResultStore()

/** 建築物番号 */
const buildingNumber = ref<string>('')

/** 建築物名称 */
const buildingName = ref<string>('')

/** 建築物所在地 */
const buildingAddress = ref<string>('')

/** 建築物用途 */
const buildingUsage = ref<BuildingUsage>(BUILDING_USAGE.UNSELECTED)

/** 建築物用途その他 */
const buildingUsageOther = ref<string>('')

/** 構造形式 */
const constructionMethod = ref<ConstructionMethod>(CONSTRUCTION_METHOD.WOOD.UNSELECTED)

/** 混合構造の追加セレクトボックスの値 (2つのセレクトボックス) */
const mixedMethod1 = ref<ConstructionMethodMixed>(CONSTRUCTION_METHOD_MIXED.UNSELECTED)
const mixedMethod2 = ref<ConstructionMethodMixed>(CONSTRUCTION_METHOD_MIXED.UNSELECTED)

/** 構造形式その他 */
const constructionMethodOther = ref<string>('')

/** 住宅地図整理番号 */
const residentialMapSerialNumber = ref<string>('')

/** 階数(木造) */
const floorCountWood = ref<FloorCountWood>(WOODEN_FLOORS.UNSELECTED)

/** 階数(木造)その他	 */
const floorCountWoodOther = ref<string>('')

/** 地上階数 */
const floorCountNonWoodAbove = ref<string>('')

/** 地下階数 */
const floorCountNonWoodBasement = ref<string>('')

/** 建築物規模 */
const buildingDimension = ref<string[]>([])

/** 必須項目の未入力リスト */
const unfilledList = ref<string[]>([BUILDING_LOCATION_SPECIFICATION_LABEL])

/** 家屋未選択フラグ */
const isSelectedBuilding = ref<boolean>(false)

const isMapError = ref<boolean>(false)

/** 構造タイプに応じた選択肢 */
const currentConstructionOptions = computed(() => {
  return constructionMethodOption[inspectionResultStore.assessmentInfo.constructionType!]
})

/** その他の判定 */
const isOtherConstructionMethod = computed(() => {
  const currentTypeOptions =
    constructionMethodOption[inspectionResultStore.assessmentInfo.constructionType!]
  const otherOption = currentTypeOptions?.find(option => option.text === OTHER_LABEL)
  return constructionMethod.value === otherOption?.value
})

const updateSelectedBuilding = (isSelected: boolean) => {
  isSelectedBuilding.value = isSelected
}

/** 地図の表示状態を管理する変数（BuildingInfoMapで設定） */
const isViewMap = ref(false)

watch(
  [isSelectedBuilding, isViewMap],
  ([newIsSelectedBuilding, newIsViewMapState]) => {
    if (!newIsSelectedBuilding && newIsViewMapState) {
      // 地図が表示されていて建物未選択の場合は未入力とする
      unfilledList.value = [BUILDING_LOCATION_SPECIFICATION_LABEL]
    } else {
      unfilledList.value = []
    }
    emit('updateUnfilledList', unfilledList.value)
  },
  { immediate: true } // 初回マウント時にも動作させる
)

watch(
  () => props.triggerValidate,
  newValue => {
    if (newValue) {
      // バリデーションなし
      emit('onValidationResult', true)
    }
  }
)

watch(
  () => props.saveToStoreFlag,
  newValue => {
    if (newValue) {
      // 建築物概要をStoreに保存
      inspectionResultStore.setBuildingOverview({
        buildingNumber: buildingNumber.value,
        buildingName: buildingName.value,
        buildingAddress: buildingAddress.value,
        buildingUsage: buildingUsage.value,
        buildingUsageOther: buildingUsageOther.value,
        constructionMethod: constructionMethod.value,
        constructionMethodOther: constructionMethodOther.value,
        constructionMethodMixed: [mixedMethod1.value, mixedMethod2.value],
        residentialMapSerialNumber: residentialMapSerialNumber.value,
        floorCountWood: floorCountWood.value,
        floorCountWoodOther: floorCountWoodOther.value,
        floorCountNonWoodAbove: floorCountNonWoodAbove.value,
        floorCountNonWoodBasement: floorCountNonWoodBasement.value,
        buildingDimensionX: buildingDimension.value[0],
        buildingDimensionY: buildingDimension.value[1]
      })
      emit('savedInspectionResultStore')
    }
  }
)

// ライフサイクル
onMounted(async () => {
  // Storeから各入力値を取得し、ref変数に反映
  setRefsFromStore()
})

/**
 * リアクティブ変数をStoreの値で初期化
 * @param なし
 */
const setRefsFromStore = (): void => {
  buildingNumber.value = inspectionResultStore.buildingOverview.buildingNumber
  buildingName.value = inspectionResultStore.buildingOverview.buildingName
  buildingAddress.value = inspectionResultStore.buildingOverview.buildingAddress
  buildingUsage.value = inspectionResultStore.buildingOverview.buildingUsage
  buildingUsageOther.value = inspectionResultStore.buildingOverview.buildingUsageOther
  constructionMethod.value = inspectionResultStore.buildingOverview.constructionMethod
  mixedMethod1.value = inspectionResultStore.buildingOverview.constructionMethodMixed[0]
  mixedMethod2.value = inspectionResultStore.buildingOverview.constructionMethodMixed[1]
  constructionMethodOther.value = inspectionResultStore.buildingOverview.constructionMethodOther
  residentialMapSerialNumber.value =
    inspectionResultStore.buildingOverview.residentialMapSerialNumber
  floorCountWood.value = inspectionResultStore.buildingOverview.floorCountWood
  floorCountWoodOther.value = inspectionResultStore.buildingOverview.floorCountWoodOther
  floorCountNonWoodAbove.value = inspectionResultStore.buildingOverview.floorCountNonWoodAbove
  floorCountNonWoodBasement.value = inspectionResultStore.buildingOverview.floorCountNonWoodBasement
  buildingDimension.value = [
    inspectionResultStore.buildingOverview.buildingDimensionX,
    inspectionResultStore.buildingOverview.buildingDimensionY
  ]
}

const isMapErrorChanged = computed(() => {
  return isMapError.value
})

const showErrCallbackMessage = (mse: string, title?: string, code?: string | number) => {
  if (code !== undefined) {
    isMapError.value = true
    return
  }
}
</script>

<template>
  <div class="container mt-5">
    <!-- 家屋情報ヘッダー -->
    <div class="d-flex justify-content-between border-bottom border-dark mb-4 pb-4">
      <h1 class="font-weight-bold my-0">
        {{ BUILDING_LOCATION_SPECIFICATION_LABEL }} <span class="text-danger">【必須】</span>
      </h1>
    </div>
    <BuildingInfoMap
      ref="buildingInfoMapRef"
      :idName="'newInspection'"
      v-model:isMapError="isMapErrorChanged"
      v-model:isViewMap="isViewMap"
      :errorCallBack="showErrCallbackMessage"
      @updateSelectedBuilding="updateSelectedBuilding"
      class="mb-5"
    />
    <!-- 調査情報ヘッダー -->
    <div class="d-flex justify-content-between border-bottom border-dark mb-5 pb-4">
      <h1 class="font-weight-bold my-0">建築物概要</h1>
    </div>
    <!-- フォーム全体 -->
    <form @submit.prevent class="text-left">
      <!-- 建築物番号 -->
      <LabelContainer name="buildingNumber" label="建築物番号" label-width="sm">
        <b-form-input v-model="buildingNumber" class="border border-dark rounded px-2 py-1 fs-4" />
      </LabelContainer>
      <!-- 建築物名称 -->
      <LabelContainer name="buildingName" label="建築物名称" label-width="sm">
        <b-form-input v-model="buildingName" class="border border-dark rounded px-2 py-1 fs-4" />
      </LabelContainer>
      <!-- 建築物所在地 -->
      <LabelContainer name="buildingAddress" label="建築物所在地" label-width="sm">
        <b-form-input v-model="buildingAddress" class="border border-dark rounded px-2 py-1 fs-4" />
      </LabelContainer>
      <!-- 建築物用途 -->
      <div class="d-flex align-items-center">
        <LabelContainer name="buildingUsage" label="建築物用途" label-width="sm">
          <b-form-select
            v-model="buildingUsage"
            :options="buildingUsageOption"
            class="border border-dark rounded px-2 py-1 fs-4"
          />
        </LabelContainer>
        <!-- 建築物用途その他 -->
      </div>
      <div v-if="buildingUsage === BUILDING_USAGE.OTHER">
        <LabelContainer name="buildingUsageOther" label="" label-width="sm">
          <b-form-input
            v-model="buildingUsageOther"
            class="border border-dark rounded px-2 py-1 fs-4"
          />
        </LabelContainer>
      </div>
      <!-- 構造形式 -->
      <div class="d-flex align-items-center">
        <LabelContainer name="constructionMethod" label="構造形式" label-width="sm">
          <b-form-select
            v-model="constructionMethod"
            :options="currentConstructionOptions"
            class="border border-dark rounded px-2 py-1 fs-4"
          />
        </LabelContainer>
      </div>
      <!-- 混合構造1 -->
      <LabelContainer
        v-if="
          inspectionResultStore.assessmentInfo.constructionType === CONSTRUCTION_TYPE.RC &&
          constructionMethod === CONSTRUCTION_METHOD.RC.MIXED
        "
        name="mixedMethod1"
        label="混合構造1"
        label-width="sm"
      >
        <b-form-select
          v-model="mixedMethod1"
          :options="mixedOption"
          class="border border-dark rounded px-2 py-1 fs-4"
        />
      </LabelContainer>
      <!-- 混合構造2 -->
      <LabelContainer
        v-if="
          inspectionResultStore.assessmentInfo.constructionType === CONSTRUCTION_TYPE.RC &&
          constructionMethod === CONSTRUCTION_METHOD.RC.MIXED
        "
        name="mixedMethod2"
        label="混合構造2"
        label-width="sm"
      >
        <b-form-select
          v-model="mixedMethod2"
          :options="mixedOption"
          class="border border-dark rounded px-2 py-1 fs-4"
        />
      </LabelContainer>
      <!-- 構造形式その他 -->
      <div v-if="isOtherConstructionMethod">
        <LabelContainer name="constructionMethodOther" label="" label-width="sm">
          <b-form-input
            v-model="constructionMethodOther"
            class="border border-dark rounded px-2 py-1 fs-4"
          />
        </LabelContainer>
      </div>
      <!-- 住宅地図整理番号 -->
      <LabelContainer name="residentialMapSerialNumber" label="住宅地図整理番号" label-width="sm">
        <b-form-input
          v-model="residentialMapSerialNumber"
          class="border border-dark rounded px-2 py-1 fs-4"
        />
      </LabelContainer>
      <!-- 階数(木造) -->
      <div
        v-if="inspectionResultStore.assessmentInfo.constructionType === CONSTRUCTION_TYPE.WOOD"
        class="d-flex align-items-center"
      >
        <LabelContainer name="floorCountWood" label="階数" label-width="sm">
          <b-form-select
            v-model="floorCountWood"
            :options="woodenFloorsOption"
            class="border border-dark rounded px-2 py-1 fs-4"
            style="width: 120px"
          />
        </LabelContainer>
        <!-- 階数(木造)その他 -->
        <div v-if="floorCountWood === WOODEN_FLOORS.OTHER">
          <LabelContainer name="floorCountWoodOther" label="" label-width="sm">
            <b-form-input
              v-model="floorCountWoodOther"
              class="border border-dark rounded px-2 py-1 fs-4"
            />
          </LabelContainer>
        </div>
      </div>
      <!-- 階数 -->
      <div v-else class="d-flex align-items-center mt-4">
        <!-- 地上階数 -->
        <div class="d-flex align-items-center mb-4 w-100">
          <label class="form-label me-3 fs-4 w-25">階数</label>
          <div class="d-flex align-items-center">
            <span class="form-label fs-4 w-25">地上</span>
            <LabelContainer name="floorCountNonWoodAbove" label="" :showLabel="false">
              <b-form-input
                id="floorCountNonWoodAbove"
                type="number"
                v-model="floorCountNonWoodAbove"
                class="bottom-border-input mx-2 fs-4"
              />
            </LabelContainer>
            <span class="fs-4 mr-3">階</span>
            <!-- 地下階数 -->
            <span class="form-label ms-3 fs-4 w-25">地下</span>
            <LabelContainer name="floorCountNonWoodBasement" label="" :showLabel="false">
              <b-form-input
                id="floorCountNonWoodBasement"
                type="number"
                v-model="floorCountNonWoodBasement"
                class="bottom-border-input mx-2 fs-4"
              />
            </LabelContainer>
            <span class="fs-4">階</span>
          </div>
        </div>
      </div>
      <!-- 建築物規模 -->
      <div class="d-flex align-items-center mb-4 w-100">
        <LabelContainer name="buildingDimension" label="建築物規模">
          <!-- 入力部分 -->
          <div class="d-flex align-items-center flex-grow-1 justify-content-end">
            <span class="fs-4 w-50">1階寸法</span>
            <span class="fs-4">約</span>
            <LabelContainer name="buildingDimensionWidth" label="" :showLabel="false">
              <b-form-input
                id="buildingDimensionWidth"
                type="number"
                v-model="buildingDimension[0]"
                class="bottom-border-input mx-2 fs-4"
              />
            </LabelContainer>
            <span class="fs-4 me-5">m</span>
            <span class="fs-3">×</span>
            <LabelContainer name="buildingDimensionLength" label="" :showLabel="false">
              <b-form-input
                id="buildingDimensionLength"
                type="number"
                v-model="buildingDimension[1]"
                class="bottom-border-input mx-2 fs-4"
              />
            </LabelContainer>
            <span class="fs-4">m</span>
          </div>
        </LabelContainer>
      </div>
    </form>
  </div>
</template>

<style scoped>
:deep(.form-select option) {
  font-size: 1.2rem;
}
</style>
